import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X, AlertCircle, GitCommit, GitBranch, AlertTriangle, CheckCircle2,
  MessageSquare, FileText, Wrench, ExternalLink, Clock, User,
  TrendingUp, Copy, Check, ChevronRight, Loader2, Sparkles,
  FileCode, Tag, Activity, Zap, BookOpen, Hash, Terminal, Code2,
  Quote, Link2, ListOrdered, Bot, ArrowRight
} from 'lucide-react';

// =============================================================================
// Details Modal - Shows detailed information when cards are clicked
// =============================================================================
function DetailsModal({ isOpen, onClose, data, type }) {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = useCallback((text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  if (!isOpen || !data) return null;

  const renderIncidentDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-destructive/10">
          <AlertCircle className="w-6 h-6 text-destructive" />
        </div>
        <div>
          <span className="text-xs font-medium text-destructive uppercase tracking-wide">
            {data.severity || 'CRITICAL'} INCIDENT
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.title}</h3>
          <p className="text-sm text-muted-foreground">{data.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Status</p>
          <p className="font-medium text-foreground">{data.status || 'Open'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Severity</p>
          <p className={`font-medium ${data.severity === 'critical' ? 'text-destructive' :
            data.severity === 'high' ? 'text-warning' :
              data.severity === 'medium' ? 'text-warning' : 'text-success'
            }`}>
            {(data.severity || 'CRITICAL').toUpperCase()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Started</p>
          <p className="font-medium text-foreground">{data.started_at || data.time || '35 minutes ago'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Impact</p>
          <p className="font-medium text-destructive">{data.impact || '$75K/hour'}</p>
        </div>
      </div>

      <div>
        <p className="text-xs text-muted-foreground uppercase mb-1">Description</p>
        <p className="text-sm text-foreground">{data.description}</p>
      </div>

      {data.affected_services && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Affected Services</p>
          <div className="flex flex-wrap gap-2">
            {data.affected_services.map((service, idx) => (
              <span key={idx} className="badge">
                {service}
              </span>
            ))}
          </div>
        </div>
      )}

      {data.linked_resources && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Linked Resources</p>
          <div className="space-y-2">
            {data.linked_resources.map((resource, idx) => (
              <a key={idx} href={resource.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline">
                {resource.type === 'jira' && <Tag className="w-4 h-4" />}
                {resource.type === 'slack' && <MessageSquare className="w-4 h-4" />}
                {resource.label}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderGitHubCommitDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-secondary">
          <GitCommit className="w-6 h-6 text-foreground" />
        </div>
        <div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            GITHUB COMMIT
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.message || data.title}</h3>
          <p className="text-sm text-muted-foreground font-mono">{data.sha || data.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Author</p>
          <p className="font-medium text-foreground">{data.author}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Committed</p>
          <p className="font-medium text-foreground">{data.timestamp || data.time}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Service</p>
          <p className="font-medium text-foreground">{data.service || 'payment-gateway'}</p>
        </div>
        {data.pr_number && (
          <div>
            <p className="text-xs text-muted-foreground uppercase">PR Number</p>
            <a href={data.pr_url} className="font-medium text-primary hover:underline">
              #{data.pr_number}
            </a>
          </div>
        )}
      </div>

      {data.files_changed && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Files Changed</p>
          <div className="bg-secondary rounded-lg p-3 font-mono text-sm">
            {data.files_changed.map((file, idx) => (
              <div key={idx} className="text-foreground">{file}</div>
            ))}
          </div>
        </div>
      )}

      {data.change_summary && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Change Summary</p>
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-sm text-warning">
            {data.change_summary}
          </div>
        </div>
      )}

      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center">
          View on GitHub
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderGitHubPRDetails = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-success/10">
          <GitBranch className="w-6 h-6 text-success" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-success uppercase tracking-wide">
              {data.source_type === 'gitlab_mr' ? 'GITLAB MERGE REQUEST' : 'GITHUB PULL REQUEST'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.state === 'open' ? 'bg-success/10 text-success' :
              data.merged || data.state === 'merged' ? 'bg-primary/10 text-primary' :
                data.state === 'closed' ? 'bg-destructive/10 text-destructive' :
                  'bg-secondary text-muted-foreground'
              }`}>
              {data.merged ? 'merged' : data.state || 'unknown'}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-foreground mt-1">{data.title}</h3>
          {data.number && <p className="text-sm text-muted-foreground">#{data.number} • {data.repo}</p>}
        </div>
      </div>

      {/* Author with Avatar */}
      <div className="flex items-center gap-3 bg-secondary rounded-lg p-3">
        {data.author_avatar ? (
          <img src={data.author_avatar} alt={data.author} className="w-10 h-10 rounded-full" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <div>
          <p className="font-medium text-foreground">{data.author || 'Unknown'}</p>
          <p className="text-xs text-muted-foreground">
            Created {data.created_at ? new Date(data.created_at).toLocaleDateString() : 'Unknown'}
            {data.merged_at && ` • Merged ${new Date(data.merged_at).toLocaleDateString()}`}
          </p>
        </div>
      </div>

      {/* Code Stats */}
      {(data.additions > 0 || data.deletions > 0 || data.changed_files > 0) && (
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase mb-3">Code Impact</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-success">+{data.additions || 0}</p>
              <p className="text-xs text-muted-foreground">additions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">-{data.deletions || 0}</p>
              <p className="text-xs text-muted-foreground">deletions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">{data.changed_files || 0}</p>
              <p className="text-xs text-muted-foreground">files</p>
            </div>
          </div>
        </div>
      )}

      {/* Branch Info */}
      {data.head_ref && data.base_ref && (
        <div className="flex items-center gap-2 bg-secondary rounded-lg p-3">
          <GitBranch className="w-4 h-4 text-muted-foreground" />
          <code className="text-sm text-primary">{data.head_ref}</code>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <code className="text-sm text-foreground">{data.base_ref}</code>
        </div>
      )}

      {/* Labels */}
      {data.labels && data.labels.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Labels</p>
          <div className="flex flex-wrap gap-2">
            {data.labels.map((label, idx) => (
              <span
                key={idx}
                className="badge badge-teal"
              >
                {typeof label === 'string' ? label : label.name || label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {data.description && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Description</p>
          <div className="bg-secondary rounded-lg p-3 text-sm text-foreground prose prose-sm max-w-none">
            <p className="whitespace-pre-wrap">{data.description}</p>
          </div>
        </div>
      )}

      {/* View on GitHub */}
      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center bg-success hover:bg-success/90">
          View on {data.source_type === 'gitlab_mr' ? 'GitLab' : 'GitHub'}
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderGitHubWorkflowDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Activity className="w-6 h-6 text-primary" />
        </div>
        <div>
          <span className="text-xs font-medium text-primary uppercase tracking-wide">
            GITHUB WORKFLOW RUN
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.title}</h3>
          {data.run_number && <p className="text-sm text-muted-foreground">Run #{data.run_number}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Conclusion</p>
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${data.conclusion === 'success' ? 'bg-success/10 text-success' :
            data.conclusion === 'failure' ? 'bg-destructive/10 text-destructive' :
              data.conclusion === 'cancelled' ? 'bg-secondary text-muted-foreground' :
                'bg-warning/10 text-warning'
            }`}>
            {data.conclusion || 'running'}
          </span>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Branch</p>
          <p className="font-medium text-foreground font-mono">{data.branch || 'main'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Started</p>
          <p className="font-medium text-foreground">{data.created_at || data.time}</p>
        </div>
      </div>

      {data.repo && (
        <div>
          <p className="text-xs text-muted-foreground uppercase">Repository</p>
          <p className="font-medium text-foreground font-mono">{data.repo}</p>
        </div>
      )}

      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center">
          View on GitHub Actions
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderSuggestedFixDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-success/10">
          <CheckCircle2 className="w-6 h-6 text-success" />
        </div>
        <div>
          <span className="text-xs font-medium text-success uppercase tracking-wide">
            SUGGESTED FIX
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.title || 'Configuration Rollback'}</h3>
        </div>
      </div>

      {data.options && data.options.map((option, idx) => (
        <div key={idx} className="space-y-2">
          <p className="text-sm font-medium text-foreground">Option {idx + 1}: {option.label}</p>
          <div className="bg-card border border-border rounded-lg p-3 font-mono text-sm relative group">
            <pre className="text-success whitespace-pre-wrap">{option.command}</pre>
            <button
              onClick={() => copyToClipboard(option.command, `opt-${idx}`)}
              className="absolute top-2 right-2 p-1.5 bg-secondary rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {copied === `opt-${idx}` ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>
      ))}

      {data.estimated_impact && (
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="bg-secondary rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground uppercase">Recovery Time</p>
            <p className="text-lg font-semibold text-primary">{data.estimated_impact.recovery_time || '~5 min'}</p>
          </div>
          <div className="bg-secondary rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground uppercase">Risk Level</p>
            <p className={`text-lg font-semibold ${data.estimated_impact.risk === 'Low' ? 'text-success' :
              data.estimated_impact.risk === 'Medium' ? 'text-warning' : 'text-destructive'
              }`}>
              {data.estimated_impact.risk || 'Low'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderRootCauseDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-warning/10">
          <AlertTriangle className="w-6 h-6 text-warning" />
        </div>
        <div>
          <span className="text-xs font-medium text-warning uppercase tracking-wide">
            ROOT CAUSE IDENTIFIED
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.title}</h3>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Confidence:</span>
        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-warning rounded-full"
            style={{ width: `${(data.confidence || 0.96) * 100}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-warning">
          {Math.round((data.confidence || 0.96) * 100)}%
        </span>
      </div>

      <div>
        <p className="text-xs text-muted-foreground uppercase mb-1">Service</p>
        <p className="font-medium text-foreground">{data.service}</p>
      </div>

      {data.metrics && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Impact Metrics</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(data.metrics).map(([key, value]) => (
              <div key={key} className="bg-secondary rounded p-2">
                <p className="text-xs text-muted-foreground">{key}</p>
                <p className="font-medium text-foreground">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.evidence && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-1">Evidence</p>
          <p className="text-sm text-foreground">{data.evidence}</p>
        </div>
      )}
    </div>
  );

  const renderJiraDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-info/10">
          <Tag className="w-6 h-6 text-info" />
        </div>
        <div>
          <span className="text-xs font-medium text-info uppercase tracking-wide">
            JIRA TICKET
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.key || data.id}</h3>
        </div>
      </div>

      <p className="text-sm text-foreground">{data.summary || data.title}</p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Status</p>
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${data.status === 'Done' ? 'bg-success/10 text-success' :
            data.status === 'In Progress' ? 'bg-info/10 text-info' :
              'bg-secondary text-foreground'
            }`}>
            {data.status}
          </span>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Priority</p>
          <p className="font-medium text-foreground">{data.priority || 'High'}</p>
        </div>
      </div>

      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center bg-info hover:bg-info/90">
          Open in Jira
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderSlackDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <MessageSquare className="w-6 h-6 text-primary" />
        </div>
        <div>
          <span className="text-xs font-medium text-primary uppercase tracking-wide">
            SLACK DISCUSSION
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.channel}</h3>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Messages</p>
          <p className="font-medium text-foreground">{data.message_count || '47 messages'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Participants</p>
          <p className="font-medium text-foreground">{data.participants || '3 participants'}</p>
        </div>
      </div>

      {data.key_points && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-2">Key Discussion Points</p>
          <ul className="space-y-1">
            {data.key_points.map((point, idx) => (
              <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center">
          Open in Slack
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderRunbookDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <BookOpen className="w-6 h-6 text-primary" />
        </div>
        <div>
          <span className="text-xs font-medium text-primary uppercase tracking-wide">
            RUNBOOK
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.id || data.title}</h3>
        </div>
      </div>

      <div>
        <p className="text-xs text-muted-foreground uppercase mb-1">Steps</p>
        <p className="font-medium text-foreground">{data.steps || '6'} documented steps</p>
      </div>

      {data.summary && (
        <div>
          <p className="text-xs text-muted-foreground uppercase mb-1">Summary</p>
          <p className="text-sm text-foreground">{data.summary}</p>
        </div>
      )}

      {data.url && (
        <a href={data.url} target="_blank" rel="noopener noreferrer"
          className="btn btn-primary w-full justify-center">
          View Runbook
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const renderMetricDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-info/10">
          <Activity className="w-6 h-6 text-info" />
        </div>
        <div>
          <span className="text-xs font-medium text-info uppercase tracking-wide">
            METRIC ANOMALY
          </span>
          <h3 className="text-lg font-semibold text-foreground">{data.title}</h3>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Confidence:</span>
        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-info rounded-full"
            style={{ width: `${(data.confidence || 0.94) * 100}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-info">
          {Math.round((data.confidence || 0.94) * 100)}%
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase">Service</p>
          <p className="font-medium text-foreground">{data.service}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase">Change</p>
          <p className="font-medium text-destructive">{data.change || 'Error rate: 1% → 23%'}</p>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'incident': return renderIncidentDetails();
      case 'github_commit': return renderGitHubCommitDetails();
      case 'github_pr': return renderGitHubPRDetails();
      case 'github_workflow': return renderGitHubWorkflowDetails();
      case 'suggested_fix': return renderSuggestedFixDetails();
      case 'root_cause': return renderRootCauseDetails();
      case 'jira': return renderJiraDetails();
      case 'slack': return renderSlackDetails();
      case 'runbook': return renderRunbookDetails();
      case 'metric': return renderMetricDetails();
      default: return <p>Unknown type</p>;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-foreground">Details</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Node Cards - Visual representation of each element in the causal chain
// =============================================================================
function CausalNodeCard({ type, data, onClick, position = 'left', isHighlighted = false }) {
  const getCardStyles = () => {
    const base = "relative cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 rounded-lg border overflow-hidden";

    switch (type) {
      case 'incident':
        return `${base} bg-card border-destructive/30 ${isHighlighted ? 'ring-2 ring-destructive' : ''}`;
      case 'github_commit':
        return `${base} bg-card border-border ${isHighlighted ? 'ring-2 ring-border' : ''}`;
      case 'github_pr':
        return `${base} bg-card border-success/30 ${isHighlighted ? 'ring-2 ring-success' : ''}`;
      case 'github_workflow':
        return `${base} bg-card border-primary/30 ${isHighlighted ? 'ring-2 ring-primary' : ''}`;
      case 'root_cause':
        return `${base} bg-warning/5 border-warning/40 ${isHighlighted ? 'ring-2 ring-warning' : ''}`;
      case 'metric':
        return `${base} bg-card border-info/30 ${isHighlighted ? 'ring-2 ring-info' : ''}`;
      case 'jira':
        return `${base} bg-card border-info/30 ${isHighlighted ? 'ring-2 ring-info' : ''}`;
      case 'slack':
        return `${base} bg-card border-primary/30 ${isHighlighted ? 'ring-2 ring-primary' : ''}`;
      case 'runbook':
        return `${base} bg-card border-primary/30 ${isHighlighted ? 'ring-2 ring-primary' : ''}`;
      case 'suggested_fix':
        return `${base} bg-success/5 border-success/40 ${isHighlighted ? 'ring-2 ring-success' : ''}`;
      default:
        return `${base} bg-card border-border`;
    }
  };

  const renderIncidentCard = () => (
    <div className="p-3 space-y-2 min-w-[200px]">
      <div className="flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-destructive" />
        <span className="text-xs font-medium text-destructive uppercase">
          {data.severity || 'Critical'} Incident
        </span>
      </div>
      <h4 className="font-semibold text-foreground text-sm leading-tight">{data.title}</h4>
      <p className="text-xs text-muted-foreground">{data.id}</p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="text-destructive font-medium">{data.failure_rate || '23% failure rate'}</span>
        <span>{data.time || '35 min ago'}</span>
      </div>
    </div>
  );

  const renderGitHubCommitCard = () => (
    <div className="p-3 space-y-2 min-w-[180px]">
      <div className="flex items-center gap-2">
        <GitCommit className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase">GitHub Commit</span>
      </div>
      <h4 className="font-medium text-foreground text-sm">{data.message || data.title}</h4>
      <p className="text-xs text-muted-foreground">{data.author}</p>
      <p className="text-xs text-muted-foreground/70">{data.time || '2h 15m before incident'}</p>
    </div>
  );

  const renderGitHubPRCard = () => (
    <div className="p-4 space-y-3 min-w-[260px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-success" />
          <span className="text-xs font-medium text-success uppercase tracking-wide">
            {data.source_type === 'gitlab_mr' ? 'GitLab MR' : 'GitHub PR'}
          </span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.state === 'open' ? 'bg-success/10 text-success' :
          data.merged || data.state === 'merged' ? 'bg-primary/10 text-primary' :
            data.state === 'closed' ? 'bg-destructive/10 text-destructive' :
              'bg-secondary text-muted-foreground'
          }`}>
          {data.merged ? 'merged' : data.state || 'unknown'}
        </span>
      </div>

      {/* Title and Number */}
      <div>
        <h4 className="font-semibold text-foreground text-sm leading-tight">{data.title}</h4>
        {data.number && (
          <p className="text-xs text-muted-foreground mt-0.5">#{data.number} • {data.repo}</p>
        )}
      </div>

      {/* Author */}
      <div className="flex items-center gap-2">
        {data.author_avatar ? (
          <img src={data.author_avatar} alt={data.author} className="w-5 h-5 rounded-full" />
        ) : (
          <User className="w-4 h-4 text-muted-foreground" />
        )}
        <span className="text-xs text-muted-foreground">by {data.author || 'Unknown'}</span>
      </div>

      {/* Code Changes Stats */}
      {(data.additions > 0 || data.deletions > 0 || data.changed_files > 0) && (
        <div className="flex items-center gap-3 text-xs bg-secondary rounded-md px-2 py-1.5">
          <span className="text-success font-mono font-medium">+{data.additions || 0}</span>
          <span className="text-destructive font-mono font-medium">-{data.deletions || 0}</span>
          {data.changed_files > 0 && (
            <span className="text-muted-foreground flex items-center gap-1">
              <FileCode className="w-3 h-3" />
              {data.changed_files} files
            </span>
          )}
        </div>
      )}

      {/* Branch Info */}
      {data.head_ref && data.base_ref && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 font-mono">
          <span className="text-primary">{data.head_ref}</span>
          <ChevronRight className="w-3 h-3" />
          <span>{data.base_ref}</span>
        </div>
      )}

      {/* View on GitHub Link */}
      {data.url && (
        <a
          href={data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-success hover:text-success/80 hover:underline flex items-center gap-1 font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="w-3 h-3" />
          View on GitHub
        </a>
      )}
    </div>
  );

  const renderGitHubWorkflowCard = () => (
    <div className="p-3 space-y-2 min-w-[200px]">
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-primary uppercase">Workflow Run</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${data.conclusion === 'success' ? 'bg-success/10 text-success' :
          data.conclusion === 'failure' ? 'bg-destructive/10 text-destructive' :
            data.conclusion === 'cancelled' ? 'bg-secondary text-muted-foreground' :
              'bg-warning/10 text-warning'
          }`}>
          {data.conclusion || 'running'}
        </span>
      </div>
      <h4 className="font-semibold text-foreground text-sm leading-tight">{data.title}</h4>
      {data.run_number && <p className="text-xs text-muted-foreground">Run #{data.run_number}</p>}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>Branch: {data.branch || 'main'}</span>
      </div>
      {data.url && (
        <a
          href={data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="w-3 h-3" />
          View on GitHub
        </a>
      )}
    </div>
  );

  const renderRootCauseCard = () => (
    <div className="p-3 space-y-2 min-w-[200px]">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-warning" />
        <span className="text-xs font-medium text-warning uppercase">Root Cause Identified</span>
      </div>
      <h4 className="font-semibold text-foreground text-sm">{data.title}</h4>
      <p className="text-xs text-muted-foreground">{data.service}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs font-semibold text-warning">
          Confidence: {Math.round((data.confidence || 0.96) * 100)}%
        </span>
      </div>
      {data.impact && (
        <p className="text-xs text-muted-foreground">{data.impact}</p>
      )}
    </div>
  );

  const renderMetricCard = () => (
    <div className="p-3 space-y-2 min-w-[180px]">
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 text-info" />
        <span className="text-xs text-muted-foreground">{data.title}</span>
      </div>
      <p className="text-xs text-muted-foreground">{data.service}</p>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-info">
          Confidence: {Math.round((data.confidence || 0.94) * 100)}%
        </span>
      </div>
      <p className="text-xs text-destructive">{data.change || 'Error rate: 1% → 23%'}</p>
    </div>
  );

  const renderJiraCard = () => (
    <div className="p-3 space-y-2 min-w-[180px]">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-info" />
        <span className="text-xs font-medium text-info uppercase">Jira Ticket</span>
      </div>
      <h4 className="font-medium text-foreground text-sm">{data.key || data.id}</h4>
      <p className="text-xs text-muted-foreground line-clamp-2">{data.summary || data.title}</p>
      <span className={`inline-block text-xs px-1.5 py-0.5 rounded ${data.status === 'In Progress' ? 'bg-info/10 text-info' :
        data.status === 'Done' ? 'bg-success/10 text-success' :
          'bg-secondary text-muted-foreground'
        }`}>
        Status: {data.status || 'In Progress'}
      </span>
    </div>
  );

  const renderSlackCard = () => (
    <div className="p-3 space-y-2 min-w-[160px]">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-primary uppercase">Slack Discussion</span>
      </div>
      <p className="font-medium text-foreground text-sm">{data.channel}</p>
      <p className="text-xs text-muted-foreground">{data.message_count || '47 messages'}, {data.participants || '3 participants'}</p>
      {data.key_message && (
        <p className="text-xs text-muted-foreground italic">"{data.key_message}"</p>
      )}
    </div>
  );

  const renderRunbookCard = () => (
    <div className="p-3 space-y-2 min-w-[140px]">
      <div className="flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-primary uppercase">Runbook</span>
      </div>
      <p className="font-medium text-foreground text-sm">{data.id || data.title}</p>
      <p className="text-xs text-muted-foreground">{data.steps || '6'} documented steps</p>
    </div>
  );

  const renderSuggestedFixCard = () => (
    <div className="p-3 space-y-2 min-w-[180px]">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-success" />
        <span className="text-xs font-medium text-success uppercase">Suggested Fix</span>
      </div>
      <h4 className="font-medium text-foreground text-sm">{data.action || data.title}</h4>
      <p className="text-xs text-muted-foreground">{data.description}</p>
      {data.file_reference && (
        <p className="text-xs text-primary font-mono">{data.file_reference}</p>
      )}
    </div>
  );

  const renderCard = () => {
    switch (type) {
      case 'incident': return renderIncidentCard();
      case 'github_commit': return renderGitHubCommitCard();
      case 'github_pr': return renderGitHubPRCard();
      case 'github_workflow': return renderGitHubWorkflowCard();
      case 'root_cause': return renderRootCauseCard();
      case 'metric': return renderMetricCard();
      case 'jira': return renderJiraCard();
      case 'slack': return renderSlackCard();
      case 'runbook': return renderRunbookCard();
      case 'suggested_fix': return renderSuggestedFixCard();
      default: return null;
    }
  };

  return (
    <div className={getCardStyles()} onClick={() => onClick(type, data)}>
      {renderCard()}
    </div>
  );
}

// =============================================================================
// Connection Arrow - Visual connection between nodes
// =============================================================================
function ConnectionArrow({ label, direction = 'right', className = '' }) {
  return (
    <div className={`flex items-center ${direction === 'down' ? 'flex-col' : ''} ${className}`}>
      <div className={`${direction === 'down' ? 'h-8 w-0.5' : 'w-12 h-0.5'} bg-border relative`}>
        {label && (
          <span className={`absolute text-xs text-muted-foreground whitespace-nowrap ${direction === 'down' ? 'left-2 top-1/2 -translate-y-1/2' : '-top-4 left-1/2 -translate-x-1/2'
            }`}>
            {label}
          </span>
        )}
      </div>
      <ChevronRight className={`w-4 h-4 text-muted-foreground ${direction === 'down' ? 'rotate-90' : ''}`} />
    </div>
  );
}

// =============================================================================
// Code Preview Panel - Shows the actual code changes
// =============================================================================
function CodePreviewPanel({ data }) {
  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-secondary border-b border-border">
        <GitBranch className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-foreground">{data.service || 'payment-gateway'}</span>
        <span className="text-muted-foreground">/</span>
        <span className="text-sm text-primary">{data.file_path || 'config/database.properties'}</span>
        {data.url && (
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="ml-auto">
            <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </a>
        )}
      </div>
      <div className="p-4 font-mono text-sm">
        {data.commit_info && (
          <div className="text-xs text-muted-foreground mb-3">
            Commit {data.commit_sha || '7a3f9e2'} by {data.author || 'james.wilson@acme.com'}
          </div>
        )}
        {data.code_lines ? (
          data.code_lines.map((line, idx) => (
            <div key={idx} className={`flex ${line.type === 'comment' ? 'text-muted-foreground' :
              line.type === 'highlight' ? 'bg-warning/10 text-warning' :
                line.type === 'added' ? 'bg-success/10 text-success' :
                  line.type === 'removed' ? 'bg-destructive/10 text-destructive' :
                    'text-foreground'
              }`}>
              <span className="w-8 text-right pr-3 text-muted-foreground select-none">{line.number}</span>
              <span className="flex-1">{line.content}</span>
            </div>
          ))
        ) : (
          <pre className="text-foreground whitespace-pre-wrap">{data.code || '# No code available'}</pre>
        )}
        {data.related_pr && (
          <div className="mt-4 pt-4 border-t border-border">
            <a href={data.related_pr.url} className="text-xs text-primary hover:underline flex items-center gap-1">
              <GitBranch className="w-3 h-3" />
              Related PR: #{data.related_pr.number} "{data.related_pr.title}"
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Code Changes Section - Shows files changed in PRs
// =============================================================================
function CodeChangesSection({ changes, sourceType }) {
  const [expandedFiles, setExpandedFiles] = useState({});
  const [copiedFile, setCopiedFile] = useState(null);

  const toggleFile = useCallback((idx) => {
    setExpandedFiles(prev => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const copyPatch = useCallback((patch, idx) => {
    navigator.clipboard.writeText(patch);
    setCopiedFile(idx);
    setTimeout(() => setCopiedFile(null), 2000);
  }, []);

  if (!changes || changes.length === 0) return null;

  // Don't show for workflows or non-PR types
  if (sourceType && !['github_pr', 'gitlab_mr'].includes(sourceType)) return null;

  // Calculate totals
  const totalAdditions = changes.reduce((sum, c) => sum + (c.additions || 0), 0);
  const totalDeletions = changes.reduce((sum, c) => sum + (c.deletions || 0), 0);
  const totalFiles = changes.length;

  // Get file extension icon
  const getFileIcon = (filepath) => {
    const ext = filepath?.split('.').pop()?.toLowerCase() || '';
    const iconMap = {
      js: '🟨', jsx: '⚛️', ts: '🔷', tsx: '⚛️',
      py: '🐍', rb: '💎', go: '🔵', rs: '🦀',
      java: '☕', kt: '🟣', swift: '🍎',
      css: '🎨', scss: '💅', html: '🌐',
      json: '📋', yaml: '📄', yml: '📄',
      md: '📝', sql: '🗄️', sh: '🖥️',
    };
    return iconMap[ext] || '📄';
  };

  return (
    <div className="card mb-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
            <FileCode className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-medium text-foreground text-sm">Code Changes</h3>
            <p className="text-muted-foreground text-xs">
              {totalFiles} {totalFiles === 1 ? 'file' : 'files'} modified
            </p>
          </div>
        </div>

        {/* Stats summary */}
        <div className="flex items-center gap-3">
          <span className="text-success font-mono text-sm font-medium">+{totalAdditions}</span>
          <span className="text-destructive font-mono text-sm font-medium">-{totalDeletions}</span>
        </div>
      </div>

      {/* File list */}
      <div className="divide-y divide-border">
        {changes.map((change, idx) => {
          const isExpanded = expandedFiles[idx];
          const hasPatch = !!change.patch;
          const additions = change.additions || 0;
          const deletions = change.deletions || 0;
          const total = additions + deletions;
          const additionPercent = total > 0 ? (additions / total) * 100 : 50;

          return (
            <div key={idx}>
              {/* File row */}
              <div
                className={`flex items-center gap-3 px-4 py-2.5 ${hasPatch ? 'cursor-pointer hover:bg-secondary/50' : ''
                  }`}
                onClick={() => hasPatch && toggleFile(idx)}
              >
                {/* File icon */}
                <span className="text-base flex-shrink-0">{getFileIcon(change.file_path)}</span>

                {/* File path */}
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-foreground truncate">
                    {change.file_path}
                  </p>
                </div>

                {/* Status badge */}
                {change.status && change.status !== 'summary' && (
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${change.status === 'added' ? 'bg-success/10 text-success' :
                    change.status === 'removed' ? 'bg-destructive/10 text-destructive' :
                      change.status === 'renamed' ? 'bg-info/10 text-info' :
                        'bg-warning/10 text-warning'
                    }`}>
                    {change.status}
                  </span>
                )}

                {/* Changes indicator */}
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="text-success">+{additions}</span>
                  <span className="text-destructive">-{deletions}</span>
                </div>

                {/* Expand indicator */}
                {hasPatch && (
                  <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                )}
              </div>

              {/* Expanded diff view */}
              {isExpanded && hasPatch && (
                <div className="bg-card border-t border-border">
                  {/* Diff header */}
                  <div className="flex items-center justify-between px-4 py-2 bg-secondary border-b border-border">
                    <span className="text-xs text-muted-foreground font-mono">{change.file_path}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyPatch(change.patch, idx);
                      }}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all ${copiedFile === idx
                        ? 'bg-success/20 text-success'
                        : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
                        }`}
                    >
                      {copiedFile === idx ? (
                        <>
                          <Check className="w-3 h-3" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>

                  {/* Diff content */}
                  <div className="overflow-x-auto">
                    <pre className="p-4 text-xs font-mono leading-relaxed">
                      {change.patch.split('\n').map((line, lineIdx) => {
                        let lineClass = 'text-muted-foreground';
                        let bgClass = '';

                        if (line.startsWith('+') && !line.startsWith('+++')) {
                          lineClass = 'text-success';
                          bgClass = 'bg-success/10';
                        } else if (line.startsWith('-') && !line.startsWith('---')) {
                          lineClass = 'text-destructive';
                          bgClass = 'bg-destructive/10';
                        } else if (line.startsWith('@')) {
                          lineClass = 'text-info';
                          bgClass = 'bg-info/5';
                        }

                        return (
                          <div
                            key={lineIdx}
                            className={`${lineClass} ${bgClass} px-2 py-0.5 -mx-2`}
                          >
                            <span className="select-none text-muted-foreground/50 mr-3">{String(lineIdx + 1).padStart(3)}</span>
                            {line || ' '}
                          </div>
                        );
                      })}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Syntax Highlighter - Beautiful code highlighting without external deps
// =============================================================================
const syntaxHighlight = (code, language) => {
  if (!code) return [];

  // Language-specific keyword sets
  const languageKeywords = {
    javascript: /\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|extends|import|export|from|as|default|async|await|try|catch|throw|finally|new|this|typeof|instanceof|delete|void|yield|in|of|static|get|set|super)\b/g,
    typescript: /\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|extends|implements|import|export|from|as|default|async|await|try|catch|throw|finally|new|this|typeof|instanceof|delete|void|yield|in|of|static|get|set|super|type|interface|enum|namespace|declare|readonly|private|public|protected|abstract)\b/g,
    python: /\b(def|class|return|if|elif|else|for|while|try|except|finally|raise|import|from|as|with|pass|break|continue|lambda|yield|global|nonlocal|assert|True|False|None|and|or|not|in|is|async|await|self|cls)\b/g,
    java: /\b(public|private|protected|static|final|abstract|class|interface|extends|implements|new|return|if|else|for|while|do|switch|case|break|continue|try|catch|finally|throw|throws|import|package|this|super|void|boolean|byte|char|short|int|long|float|double|null|true|false|instanceof|synchronized|volatile|transient|native|strictfp|enum|assert)\b/g,
    go: /\b(func|return|if|else|for|range|switch|case|default|break|continue|fallthrough|goto|var|const|type|struct|interface|map|chan|package|import|defer|go|select|make|new|append|len|cap|copy|delete|close|panic|recover|nil|true|false|iota)\b/g,
    rust: /\b(fn|let|mut|const|static|return|if|else|loop|while|for|in|match|break|continue|struct|enum|impl|trait|type|use|mod|pub|crate|self|super|where|as|unsafe|async|await|move|ref|dyn|box|true|false|Some|None|Ok|Err)\b/g,
    sql: /\b(SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|OUTER|FULL|CROSS|ON|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|TABLE|INDEX|VIEW|DATABASE|ALTER|DROP|ADD|COLUMN|PRIMARY|KEY|FOREIGN|REFERENCES|NOT|NULL|UNIQUE|DEFAULT|CHECK|CONSTRAINT|CASCADE|AND|OR|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|UNION|ALL|DISTINCT|AS|LIKE|BETWEEN|IN|EXISTS|IS|COUNT|SUM|AVG|MAX|MIN|CASE|WHEN|THEN|ELSE|END|ASC|DESC|COMMIT|ROLLBACK|TRANSACTION|GRANT|REVOKE|TRUNCATE)\b/gi,
    bash: /\b(if|then|else|elif|fi|for|in|do|done|while|until|case|esac|function|return|local|export|source|alias|unalias|echo|printf|read|declare|typeset|readonly|shift|set|unset|trap|exit|eval|exec|true|false|test)\b/g,
  };

  // Token definitions
  const patterns = {
    keyword: languageKeywords[language] || languageKeywords.javascript,
    string: /(['"`])((?:\\.|(?!\1)[^\\])*?)\1/g,
    templateLiteral: /`(?:[^`\\]|\\.)*`/g,
    comment: /(\/\/.*$|\/\*[\s\S]*?\*\/|#(?!!).*$|--.*$|"""[\s\S]*?"""|'''[\s\S]*?''')/gm,
    number: /\b(\d+\.?\d*(?:e[+-]?\d+)?|0x[a-fA-F0-9]+|0b[01]+|0o[0-7]+)\b/gi,
    function: /\b([a-zA-Z_]\w*)\s*(?=\()/g,
    decorator: /@\w+(?:\.\w+)*/g,
    operator: /([+\-*/%=<>!&|^~?:]+|=>|\.\.\.|\.\.)/g,
    property: /\.([a-zA-Z_]\w*)/g,
    type: /\b(string|number|boolean|int|float|double|char|void|bool|str|list|dict|tuple|set|Array|Object|String|Number|Boolean|Promise|Date|Error|Map|Set|WeakMap|WeakSet|Symbol|BigInt|any|unknown|never|undefined|null|Vec|Box|Rc|Arc|Option|Result)\b/g,
    builtin: /\b(console|window|document|Math|JSON|Date|Array|Object|String|Number|Boolean|RegExp|Error|Promise|Map|Set|Symbol|Proxy|Reflect|process|module|require|exports|__dirname|__filename|print|len|range|enumerate|zip|map|filter|sorted|reversed|sum|min|max|abs|all|any|open|input|type|isinstance|hasattr|getattr|setattr|delattr|super|property|classmethod|staticmethod)\b/g,
    className: /\b([A-Z][a-zA-Z0-9_]*)\b/g,
  };

  // Color map for token types (dark theme colors)
  const colors = {
    keyword: 'text-pink-400 font-medium',
    string: 'text-emerald-400',
    templateLiteral: 'text-emerald-400',
    comment: 'text-slate-500 italic',
    number: 'text-amber-300',
    function: 'text-blue-400',
    decorator: 'text-yellow-400',
    operator: 'text-cyan-300',
    property: 'text-violet-400',
    type: 'text-teal-400',
    builtin: 'text-orange-400',
    className: 'text-yellow-300',
  };

  // Simple token-based highlighting
  const tokens = [];
  let remaining = code;
  let position = 0;

  while (remaining.length > 0) {
    let earliestMatch = null;
    let earliestType = null;
    let earliestIndex = Infinity;

    // Find the earliest match across all patterns
    for (const [type, pattern] of Object.entries(patterns)) {
      pattern.lastIndex = 0;
      const match = pattern.exec(remaining);
      if (match && match.index < earliestIndex) {
        earliestMatch = match;
        earliestType = type;
        earliestIndex = match.index;
      }
    }

    if (earliestMatch) {
      // Add text before match as plain
      if (earliestIndex > 0) {
        tokens.push({ type: 'plain', text: remaining.slice(0, earliestIndex) });
      }
      // Add the matched token
      tokens.push({
        type: earliestType,
        text: earliestMatch[0],
        className: colors[earliestType] || ''
      });
      remaining = remaining.slice(earliestIndex + earliestMatch[0].length);
    } else {
      // No more matches, add remaining as plain
      tokens.push({ type: 'plain', text: remaining });
      break;
    }

    // Safety limit
    if (++position > 10000) break;
  }

  return tokens;
};

// Detect language from code content or filename
const detectLanguage = (code, hint) => {
  if (hint && hint !== 'text') return hint.toLowerCase();

  // Check for shebang
  if (code.startsWith('#!/usr/bin/env python') || code.startsWith('#!/usr/bin/python')) return 'python';
  if (code.startsWith('#!/bin/bash') || code.startsWith('#!/bin/sh')) return 'bash';
  if (code.startsWith('#!/usr/bin/env node')) return 'javascript';

  // Check for common patterns
  if (/^(import|from)\s+\w+/.test(code) && /def\s+\w+\s*\(/.test(code)) return 'python';
  if (/^package\s+\w+/.test(code) || /^func\s+\w+/.test(code)) return 'go';
  if (/^use\s+\w+::/.test(code) || /fn\s+\w+\s*\(/.test(code)) return 'rust';
  if (/^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s/i.test(code)) return 'sql';
  if (/^(public|private|protected)\s+(class|interface|enum)/.test(code)) return 'java';
  if (/^(interface|type|enum)\s+\w+/.test(code) || /:\s*(string|number|boolean|any)\b/.test(code)) return 'typescript';

  return 'javascript'; // Default
};

// =============================================================================
// Code Block - Beautiful code display with copy and syntax highlighting
// =============================================================================
function CodeBlock({ code, language = 'text', filename }) {
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Detect actual language
  const detectedLang = detectLanguage(code, language);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const lines = code.split('\n');
  const maxInitialLines = 25;
  const shouldTruncate = lines.length > maxInitialLines && !showAll;
  const displayLines = shouldTruncate ? lines.slice(0, maxInitialLines) : lines;

  // Language icon mapping
  const langIcons = {
    javascript: '🟨',
    typescript: '🔷',
    python: '🐍',
    java: '☕',
    go: '🔵',
    rust: '🦀',
    sql: '🗃️',
    bash: '💻',
    shell: '💻',
    json: '📋',
    yaml: '⚙️',
    html: '🌐',
    css: '🎨',
    markdown: '📝',
  };

  // Render highlighted code
  const renderHighlightedCode = () => {
    return displayLines.map((lineContent, lineNum) => {
      const lineTokens = syntaxHighlight(lineContent, detectedLang);
      const isHighlighted = lineContent.trim().startsWith('// TODO') ||
        lineContent.trim().startsWith('# TODO') ||
        lineContent.includes('FIXME') ||
        lineContent.includes('BUG');

      return (
        <div
          key={lineNum}
          className={`table-row group ${isHighlighted ? 'bg-yellow-500/10' : 'hover:bg-white/5'}`}
        >
          <span className="table-cell pr-4 text-slate-600 select-none text-right w-12 opacity-50 group-hover:opacity-100 transition-opacity border-r border-slate-700/50 mr-4">
            {lineNum + 1}
          </span>
          <span className="table-cell pl-4">
            {lineTokens.map((token, idx) => (
              <span key={idx} className={token.className}>{token.text}</span>
            ))}
            {lineContent === '' && <span>&nbsp;</span>}
          </span>
        </div>
      );
    });
  };

  return (
    <div className="my-4 rounded-xl overflow-hidden bg-[#0d1117] shadow-2xl ring-1 ring-slate-700/50">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#161b22] border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <div className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-xs">
            <span>{langIcons[detectedLang] || '📄'}</span>
            <span className="font-mono font-medium">{filename || detectedLang}</span>
            {lines.length > 1 && (
              <span className="text-slate-500">• {lines.length} lines</span>
            )}
          </div>
        </div>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${copied
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600 hover:text-slate-300'
            }`}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code content */}
      <div className="overflow-x-auto">
        <pre className="p-4 text-[13px] font-mono leading-relaxed">
          <code className="table text-slate-300">
            {renderHighlightedCode()}
          </code>
        </pre>
      </div>

      {/* Show more button */}
      {shouldTruncate && (
        <div className="px-4 py-2 bg-[#161b22] border-t border-slate-700/50">
          <button
            onClick={() => setShowAll(true)}
            className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
          >
            <ChevronRight className="w-3 h-3" />
            Show {lines.length - maxInitialLines} more lines
          </button>
        </div>
      )}

      {/* Collapsed indicator */}
      {showAll && lines.length > maxInitialLines && (
        <div className="px-4 py-2 bg-[#161b22] border-t border-slate-700/50">
          <button
            onClick={() => setShowAll(false)}
            className="text-xs text-slate-400 hover:text-slate-300 font-medium"
          >
            Collapse code
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Markdown Renderer - Beautifully renders markdown with delightful animations
// =============================================================================
function MarkdownRenderer({ content, animate = false }) {
  const [visibleLines, setVisibleLines] = useState(animate ? 0 : Infinity);
  const containerRef = useRef(null);

  useEffect(() => {
    if (animate && content) {
      const lines = content.split('\n').length;
      let current = 0;
      const interval = setInterval(() => {
        current += 1;
        setVisibleLines(current);
        if (current >= lines) {
          clearInterval(interval);
        }
      }, 30);
      return () => clearInterval(interval);
    }
  }, [content, animate]);

  if (!content) return null;

  // Parse markdown into structured blocks
  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // Multi-line code block (```language or ```)
      if (trimmed.startsWith('```')) {
        const langMatch = trimmed.match(/^```(\w+)?/);
        const language = langMatch?.[1] || 'text';
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        // Skip the closing ```
        if (i < lines.length) i++;

        // Detect filename from first comment or shebang
        let filename = null;
        if (codeLines.length > 0) {
          const firstLine = codeLines[0].trim();
          if (firstLine.startsWith('//') || firstLine.startsWith('#!')) {
            const match = firstLine.match(/(?:\/\/|#)\s*(?:file:?\s*)?(\S+\.\w+)/i);
            if (match) filename = match[1];
          }
        }

        blocks.push({
          type: 'codeblock',
          content: codeLines.join('\n'),
          language,
          filename
        });
        continue;
      }

      // Indented code block (4 spaces or 1 tab)
      if (line.startsWith('    ') || line.startsWith('\t')) {
        const codeLines = [];
        while (i < lines.length && (lines[i].startsWith('    ') || lines[i].startsWith('\t') || lines[i].trim() === '')) {
          if (lines[i].trim() !== '') {
            codeLines.push(lines[i].replace(/^(    |\t)/, ''));
          }
          i++;
        }
        if (codeLines.length > 0) {
          blocks.push({ type: 'codeblock', content: codeLines.join('\n'), language: 'text' });
        }
        continue;
      }

      // Blockquote
      if (trimmed.startsWith('>')) {
        const quoteLines = [];
        while (i < lines.length && lines[i].trim().startsWith('>')) {
          quoteLines.push(lines[i].trim().slice(1).trim());
          i++;
        }
        blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
        continue;
      }

      // Headers (support underline style too)
      if (trimmed.startsWith('#### ')) {
        blocks.push({ type: 'h4', content: trimmed.slice(5) });
        i++;
        continue;
      }
      if (trimmed.startsWith('### ')) {
        blocks.push({ type: 'h3', content: trimmed.slice(4).replace(/^\d+\.?\s*/, '') });
        i++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        blocks.push({ type: 'h2', content: trimmed.slice(3) });
        i++;
        continue;
      }
      if (trimmed.startsWith('# ')) {
        blocks.push({ type: 'h1', content: trimmed.slice(2) });
        i++;
        continue;
      }
      // Underline-style headers
      if (i + 1 < lines.length && lines[i + 1].trim().match(/^=+$/)) {
        blocks.push({ type: 'h1', content: trimmed });
        i += 2;
        continue;
      }
      if (i + 1 < lines.length && lines[i + 1].trim().match(/^-+$/)) {
        blocks.push({ type: 'h2', content: trimmed });
        i += 2;
        continue;
      }

      // Horizontal rule
      if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
        blocks.push({ type: 'hr' });
        i++;
        continue;
      }

      // Task list (checkbox)
      if (trimmed.match(/^[-*]\s+\[[ xX]\]\s+/)) {
        const items = [];
        while (i < lines.length) {
          const taskMatch = lines[i].trim().match(/^[-*]\s+\[([ xX])\]\s+(.+)$/);
          if (taskMatch) {
            items.push({ checked: taskMatch[1].toLowerCase() === 'x', text: taskMatch[2] });
            i++;
          } else if (lines[i].trim() === '') {
            i++;
            break;
          } else {
            break;
          }
        }
        blocks.push({ type: 'tasklist', items });
        continue;
      }

      // Numbered list (with nested content support)
      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        const items = [];
        while (i < lines.length) {
          const itemMatch = lines[i].trim().match(/^(\d+)\.\s+(.+)$/);
          if (itemMatch) {
            items.push({ num: parseInt(itemMatch[1]), text: itemMatch[2] });
            i++;
          } else if (lines[i].startsWith('   ') && items.length > 0) {
            // Continuation of previous item
            items[items.length - 1].text += ' ' + lines[i].trim();
            i++;
          } else if (lines[i].trim() === '') {
            i++;
            break;
          } else {
            break;
          }
        }
        blocks.push({ type: 'ol', items });
        continue;
      }

      // Unordered list (with nested content support)
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
        const items = [];
        let currentIndent = 0;
        while (i < lines.length) {
          const itemLine = lines[i];
          const trimmedItem = itemLine.trim();
          const indent = itemLine.search(/\S/);

          if (trimmedItem.startsWith('- ') || trimmedItem.startsWith('* ') || trimmedItem.startsWith('• ')) {
            items.push({ text: trimmedItem.slice(2), indent: indent });
            i++;
          } else if (indent > 0 && trimmedItem && items.length > 0) {
            // Continuation of previous item
            items[items.length - 1].text += ' ' + trimmedItem;
            i++;
          } else if (trimmedItem === '') {
            i++;
            // Check if next line is still part of list
            if (i < lines.length && (lines[i].trim().startsWith('-') || lines[i].trim().startsWith('*'))) {
              continue;
            }
            break;
          } else {
            break;
          }
        }
        blocks.push({ type: 'ul', items: items.map(item => item.text) });
        continue;
      }

      // Simple table (| col1 | col2 |)
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const rows = [];
        let hasHeader = false;
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          const row = lines[i].trim();
          if (row.match(/^\|[\s-:|]+\|$/)) {
            hasHeader = rows.length > 0;
            i++;
            continue;
          }
          const cells = row.split('|').slice(1, -1).map(c => c.trim());
          rows.push(cells);
          i++;
        }
        if (rows.length > 0) {
          blocks.push({ type: 'table', rows, hasHeader });
        }
        continue;
      }

      // Empty line
      if (trimmed === '') {
        i++;
        continue;
      }

      // Regular paragraph - collect consecutive non-empty lines
      const paragraphLines = [];
      while (i < lines.length && lines[i].trim() !== '' &&
        !lines[i].trim().startsWith('#') &&
        !lines[i].trim().startsWith('-') &&
        !lines[i].trim().startsWith('*') &&
        !lines[i].trim().startsWith('>') &&
        !lines[i].trim().startsWith('```') &&
        !lines[i].trim().startsWith('|') &&
        !lines[i].trim().match(/^\d+\.\s/)) {
        paragraphLines.push(lines[i].trim());
        i++;
      }
      if (paragraphLines.length > 0) {
        blocks.push({ type: 'p', content: paragraphLines.join(' ') });
      }
    }

    return blocks;
  };

  // Render inline markdown elements
  const renderInline = (text) => {
    if (!text || typeof text !== 'string') return text;

    const elements = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Find earliest match
      const patterns = [
        { regex: /\*\*\*([^*]+)\*\*\*/g, type: 'bolditalic' },
        { regex: /\*\*([^*]+)\*\*/g, type: 'bold' },
        { regex: /\*([^*]+)\*/g, type: 'italic' },
        { regex: /__([^_]+)__/g, type: 'bold' },
        { regex: /_([^_]+)_/g, type: 'italic' },
        { regex: /`([^`]+)`/g, type: 'code' },
        { regex: /\[([^\]]+)\]\(([^)]+)\)/g, type: 'link' },
      ];

      let earliestMatch = null;
      let earliestPattern = null;
      let earliestIndex = Infinity;

      for (const pattern of patterns) {
        pattern.regex.lastIndex = 0;
        const match = pattern.regex.exec(remaining);
        if (match && match.index < earliestIndex) {
          earliestMatch = match;
          earliestPattern = pattern;
          earliestIndex = match.index;
        }
      }

      if (earliestMatch) {
        // Add text before match
        if (earliestIndex > 0) {
          elements.push(<span key={key++}>{remaining.slice(0, earliestIndex)}</span>);
        }

        // Add formatted element
        switch (earliestPattern.type) {
          case 'bolditalic':
            elements.push(
              <strong key={key++} className="font-bold italic text-gray-900">{earliestMatch[1]}</strong>
            );
            break;
          case 'bold':
            elements.push(
              <strong key={key++} className="font-semibold text-gray-900">{earliestMatch[1]}</strong>
            );
            break;
          case 'italic':
            elements.push(
              <em key={key++} className="italic text-gray-600">{earliestMatch[1]}</em>
            );
            break;
          case 'code':
            elements.push(
              <code key={key++} className="px-1.5 py-0.5 bg-violet-50 text-violet-700 rounded text-[0.9em] font-mono border border-violet-100">
                {earliestMatch[1]}
              </code>
            );
            break;
          case 'link':
            elements.push(
              <a
                key={key++}
                href={earliestMatch[2]}
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-600 hover:text-violet-700 underline underline-offset-2 transition-colors"
              >
                {earliestMatch[1]}
              </a>
            );
            break;
          default:
            elements.push(<span key={key++}>{earliestMatch[0]}</span>);
        }

        remaining = remaining.slice(earliestIndex + earliestMatch[0].length);
      } else {
        elements.push(<span key={key++}>{remaining}</span>);
        break;
      }

      if (++key > 1000) break; // Safety
    }

    return elements.length > 0 ? elements : text;
  };

  // Render blocks to React elements
  const renderBlocks = (blocks) => {
    return blocks.map((block, idx) => {
      const animationStyle = animate ? {
        animationDelay: `${idx * 50}ms`,
        opacity: 0,
        animation: 'fadeSlideIn 0.3s ease-out forwards',
      } : {};

      switch (block.type) {
        case 'h1':
          return (
            <h1 key={idx} style={animationStyle} className="text-xl font-bold text-gray-900 mt-6 mb-4 pb-2 border-b border-gray-200">
              {renderInline(block.content)}
            </h1>
          );
        case 'h2':
          return (
            <h2 key={idx} style={animationStyle} className="text-lg font-semibold text-gray-900 mt-6 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
              {renderInline(block.content)}
            </h2>
          );
        case 'h3':
          return (
            <h3 key={idx} style={animationStyle} className="text-base font-semibold text-gray-800 mt-5 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full" />
              {renderInline(block.content)}
            </h3>
          );
        case 'h4':
          return (
            <h4 key={idx} style={animationStyle} className="text-sm font-medium text-gray-800 mt-4 mb-2">
              {renderInline(block.content)}
            </h4>
          );
        case 'p':
          return (
            <p key={idx} style={animationStyle} className="text-gray-700 leading-relaxed my-2">
              {renderInline(block.content)}
            </p>
          );
        case 'codeblock':
          return (
            <div key={idx} style={animationStyle}>
              <CodeBlock code={block.content} language={block.language} filename={block.filename} />
            </div>
          );
        case 'tasklist':
          return (
            <ul key={idx} style={animationStyle} className="my-3 space-y-1.5">
              {block.items.map((item, itemIdx) => (
                <li key={itemIdx} className="flex items-start gap-2 text-gray-700">
                  <span className={`mt-1 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${item.checked ? 'bg-green-100 border-green-500 text-green-600' : 'border-gray-300'
                    }`}>
                    {item.checked && <Check className="w-3 h-3" />}
                  </span>
                  <span className={`leading-relaxed ${item.checked ? 'line-through text-gray-400' : ''}`}>
                    {renderInline(item.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        case 'table':
          return (
            <div key={idx} style={animationStyle} className="my-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <tbody>
                  {block.rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className={rowIdx === 0 && block.hasHeader ? 'bg-gray-50' : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      {row.map((cell, cellIdx) => {
                        const CellTag = rowIdx === 0 && block.hasHeader ? 'th' : 'td';
                        return (
                          <CellTag
                            key={cellIdx}
                            className={`px-4 py-2 border-b border-gray-200 text-sm ${rowIdx === 0 && block.hasHeader ? 'font-semibold text-gray-900' : 'text-gray-700'
                              }`}
                          >
                            {renderInline(cell)}
                          </CellTag>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        case 'blockquote':
          return (
            <blockquote key={idx} style={animationStyle} className="my-4 pl-4 border-l-4 border-violet-300 bg-violet-50 py-3 pr-4 rounded-r-lg">
              <div className="flex items-start gap-2">
                <Quote className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                <div className="text-gray-700 italic">{renderInline(block.content)}</div>
              </div>
            </blockquote>
          );
        case 'ul':
          return (
            <ul key={idx} style={animationStyle} className="my-3 space-y-1.5">
              {block.items.map((item, itemIdx) => (
                <li key={itemIdx} className="flex items-start gap-2 text-gray-700">
                  <span className="mt-2 w-1.5 h-1.5 bg-violet-400 rounded-full flex-shrink-0" />
                  <span className="leading-relaxed">{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        case 'ol':
          return (
            <ol key={idx} style={animationStyle} className="my-3 space-y-2">
              {block.items.map((item, itemIdx) => (
                <li key={itemIdx} className="flex items-start gap-3 text-gray-700">
                  <span className="mt-0.5 w-5 h-5 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {item.num}
                  </span>
                  <span className="leading-relaxed">{renderInline(item.text)}</span>
                </li>
              ))}
            </ol>
          );
        case 'hr':
          return (
            <hr key={idx} style={animationStyle} className="my-6 border-gray-200" />
          );
        default:
          return null;
      }
    });
  };

  const blocks = parseMarkdown(content);

  return (
    <div ref={containerRef} className="markdown-content prose prose-slate max-w-none">
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {renderBlocks(blocks)}
    </div>
  );
}

// =============================================================================
// Root Cause Analysis Graph - Interactive SVG-based visualization
// =============================================================================
function RootCauseGraph({ nodes, analysis, query, onNodeClick }) {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [expandedLevel, setExpandedLevel] = useState(null);

  // Node type configuration with professional icons and colors
  const nodeConfig = {
    query: {
      label: 'Investigation Query',
      color: '#6366f1',
      bgColor: '#eef2ff',
      borderColor: '#c7d2fe',
      Icon: Sparkles,
      level: 0
    },
    incident: {
      label: 'Incident',
      color: '#dc2626',
      bgColor: '#fef2f2',
      borderColor: '#fecaca',
      Icon: AlertCircle,
      level: 1
    },
    github_pr: {
      label: 'Pull Request',
      color: '#059669',
      bgColor: '#ecfdf5',
      borderColor: '#a7f3d0',
      Icon: GitBranch,
      level: 1
    },
    github_workflow: {
      label: 'Workflow Run',
      color: '#7c3aed',
      bgColor: '#f5f3ff',
      borderColor: '#ddd6fe',
      Icon: Activity,
      level: 1
    },
    github_commit: {
      label: 'Commit',
      color: '#0891b2',
      bgColor: '#ecfeff',
      borderColor: '#a5f3fc',
      Icon: GitCommit,
      level: 2
    },
    root_cause: {
      label: 'Root Cause',
      color: '#ea580c',
      bgColor: '#fff7ed',
      borderColor: '#fed7aa',
      Icon: AlertTriangle,
      level: 3
    },
    metric: {
      label: 'Metric Impact',
      color: '#0284c7',
      bgColor: '#f0f9ff',
      borderColor: '#bae6fd',
      Icon: TrendingUp,
      level: 4
    },
    jira: {
      label: 'Jira Ticket',
      color: '#2563eb',
      bgColor: '#eff6ff',
      borderColor: '#bfdbfe',
      Icon: Tag,
      level: 2
    },
    slack: {
      label: 'Slack Thread',
      color: '#7c3aed',
      bgColor: '#f5f3ff',
      borderColor: '#ddd6fe',
      Icon: MessageSquare,
      level: 2
    },
    runbook: {
      label: 'Runbook',
      color: '#0d9488',
      bgColor: '#f0fdfa',
      borderColor: '#99f6e4',
      Icon: BookOpen,
      level: 2
    },
    suggested_fix: {
      label: 'Suggested Fix',
      color: '#16a34a',
      bgColor: '#f0fdf4',
      borderColor: '#bbf7d0',
      Icon: CheckCircle2,
      level: 5
    },
    insight: {
      label: 'Insight',
      color: '#8b5cf6',
      bgColor: '#faf5ff',
      borderColor: '#e9d5ff',
      Icon: Zap,
      level: 3
    },
  };

  // Parse LLM analysis to extract insights
  const extractInsights = (analysisText) => {
    if (!analysisText) return [];
    const text = typeof analysisText === 'string' ? analysisText : analysisText.summary || '';
    const insights = [];

    // Extract key findings
    const patterns = [
      /(?:root cause|likely cause|main issue)[:\s]+([^.\n]+)/gi,
      /(?:investigation shows|analysis reveals|found that)[:\s]+([^.\n]+)/gi,
      /(?:recommendation|suggested fix|solution)[:\s]+([^.\n]+)/gi,
    ];

    patterns.forEach(pattern => {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        if (match[1] && match[1].trim().length > 10) {
          insights.push(match[1].trim().slice(0, 60));
        }
      }
    });

    return insights.slice(0, 3);
  };

  // Build hierarchical graph structure
  const buildHierarchy = () => {
    const levels = [[], [], [], [], [], []];

    // Add query node at level 0
    if (query) {
      levels[0].push({
        id: 'query',
        type: 'query',
        title: query,
        data: { title: query }
      });
    }

    // Categorize nodes by their level
    nodes.forEach((node, idx) => {
      const config = nodeConfig[node.type] || nodeConfig.insight;
      const level = Math.min(config.level, 5);
      levels[level].push({
        id: node.data?.id || `node-${idx}`,
        type: node.type,
        title: node.data?.title || node.data?.id || config.label,
        data: node.data
      });
    });

    // Add insights if no data nodes
    if (nodes.length === 0 && analysis) {
      const insights = extractInsights(analysis);
      insights.forEach((insight, idx) => {
        levels[3].push({
          id: `insight-${idx}`,
          type: 'insight',
          title: insight,
          data: { title: insight }
        });
      });
    }

    return levels.filter(level => level.length > 0);
  };

  const hierarchy = buildHierarchy();

  // Build edges between levels
  const buildEdges = () => {
    const edges = [];
    for (let i = 0; i < hierarchy.length - 1; i++) {
      const currentLevel = hierarchy[i];
      const nextLevel = hierarchy[i + 1];

      // Connect each node in current level to relevant nodes in next level
      currentLevel.forEach(fromNode => {
        nextLevel.forEach(toNode => {
          // Determine edge type based on node relationships
          let edgeType = 'default';
          let label = '';

          if (fromNode.type === 'query') {
            edgeType = 'query';
            label = 'found';
          } else if (fromNode.type === 'incident' && toNode.type === 'root_cause') {
            edgeType = 'causal';
            label = 'caused by';
          } else if (fromNode.type === 'github_pr' && toNode.type === 'github_commit') {
            edgeType = 'contains';
            label = 'contains';
          } else if (fromNode.type === 'github_commit' && toNode.type === 'root_cause') {
            edgeType = 'trigger';
            label = 'triggered';
          } else if (toNode.type === 'suggested_fix') {
            edgeType = 'fix';
            label = 'fix';
          } else if (toNode.type === 'metric') {
            edgeType = 'impact';
            label = 'impacted';
          }

          edges.push({ from: fromNode, to: toNode, type: edgeType, label });
        });
      });
    }
    return edges;
  };

  const edges = buildEdges();

  if (hierarchy.length < 1) return null;

  // Calculate node positions for SVG
  const svgWidth = 900;
  const svgHeight = Math.max(350, hierarchy.length * 100 + 100);
  const levelHeight = svgHeight / (hierarchy.length + 1);

  const getNodePositions = () => {
    const positions = {};
    hierarchy.forEach((level, levelIdx) => {
      const y = (levelIdx + 1) * levelHeight;
      const nodeWidth = svgWidth / (level.length + 1);
      level.forEach((node, nodeIdx) => {
        positions[node.id] = {
          x: (nodeIdx + 1) * nodeWidth,
          y: y
        };
      });
    });
    return positions;
  };

  const nodePositions = getNodePositions();

  // Edge color mapping
  const edgeColors = {
    query: '#a5b4fc',
    causal: '#f87171',
    contains: '#34d399',
    trigger: '#fbbf24',
    fix: '#4ade80',
    impact: '#60a5fa',
    default: '#cbd5e1'
  };

  return (
    <div className="card mb-6 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-secondary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary rounded-xl shadow-md">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Root Cause Analysis Graph</h3>
              <p className="text-sm text-muted-foreground">Visual representation of the investigation flow</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="px-2 py-1 bg-card border border-border rounded-md">{nodes.length} entities</span>
            <span className="px-2 py-1 bg-card border border-border rounded-md">{edges.length} connections</span>
          </div>
        </div>
      </div>

      {/* SVG Flow Graph */}
      <div className="relative bg-secondary/50" style={{ minHeight: `${svgHeight}px` }}>
        <svg width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="overflow-visible">
          <defs>
            {/* Gradient definitions for edges */}
            <linearGradient id="edge-gradient-causal" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fca5a5" />
              <stop offset="100%" stopColor="#f87171" />
            </linearGradient>
            <linearGradient id="edge-gradient-fix" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#86efac" />
              <stop offset="100%" stopColor="#4ade80" />
            </linearGradient>

            {/* Arrow markers */}
            <marker id="arrow-default" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#94a3b8" />
            </marker>
            <marker id="arrow-causal" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#f87171" />
            </marker>
            <marker id="arrow-fix" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#4ade80" />
            </marker>
            <marker id="arrow-trigger" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#fbbf24" />
            </marker>

            {/* Drop shadow filter */}
            <filter id="node-shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.08" />
            </filter>
            <filter id="node-shadow-hover" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* Draw edges */}
          {edges.map((edge, idx) => {
            const fromPos = nodePositions[edge.from.id];
            const toPos = nodePositions[edge.to.id];
            if (!fromPos || !toPos) return null;

            const color = edgeColors[edge.type] || edgeColors.default;
            const markerId = `arrow-${edge.type === 'causal' ? 'causal' : edge.type === 'fix' ? 'fix' : edge.type === 'trigger' ? 'trigger' : 'default'}`;

            // Create curved path
            const midY = (fromPos.y + toPos.y) / 2;
            const controlOffset = Math.abs(toPos.x - fromPos.x) * 0.2;
            const path = `M ${fromPos.x} ${fromPos.y + 25}
                         Q ${fromPos.x} ${midY}, ${(fromPos.x + toPos.x) / 2} ${midY}
                         Q ${toPos.x} ${midY}, ${toPos.x} ${toPos.y - 25}`;

            return (
              <g key={`edge-${idx}`} className="transition-opacity duration-200">
                <path
                  d={path}
                  fill="none"
                  stroke={color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  markerEnd={`url(#${markerId})`}
                  className="transition-all duration-200"
                  style={{ opacity: hoveredNode && hoveredNode !== edge.from.id && hoveredNode !== edge.to.id ? 0.3 : 1 }}
                />
                {edge.label && (
                  <text
                    x={(fromPos.x + toPos.x) / 2}
                    y={midY - 8}
                    textAnchor="middle"
                    className="text-[10px] fill-gray-400 font-medium pointer-events-none"
                    style={{ opacity: hoveredNode && hoveredNode !== edge.from.id && hoveredNode !== edge.to.id ? 0.3 : 1 }}
                  >
                    {edge.label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Draw nodes */}
          {hierarchy.flat().map((node) => {
            const pos = nodePositions[node.id];
            if (!pos) return null;

            const config = nodeConfig[node.type] || nodeConfig.insight;
            const isHovered = hoveredNode === node.id;
            const NodeIcon = config.Icon;

            const nodeWidth = 180;
            const nodeHeight = 50;

            return (
              <g
                key={node.id}
                transform={`translate(${pos.x}, ${pos.y})`}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => node.data && onNodeClick?.(node.type, node.data)}
                style={{ transition: 'transform 0.2s ease' }}
              >
                {/* Node background */}
                <rect
                  x={-nodeWidth / 2}
                  y={-nodeHeight / 2}
                  width={nodeWidth}
                  height={nodeHeight}
                  rx="12"
                  fill={config.bgColor}
                  stroke={isHovered ? config.color : config.borderColor}
                  strokeWidth={isHovered ? 2 : 1.5}
                  filter={isHovered ? 'url(#node-shadow-hover)' : 'url(#node-shadow)'}
                  className="transition-all duration-200"
                />

                {/* Icon container */}
                <rect
                  x={-nodeWidth / 2 + 8}
                  y={-14}
                  width="28"
                  height="28"
                  rx="6"
                  fill={config.color}
                  className="transition-all duration-200"
                />

                {/* Render icon using foreignObject for React icons */}
                <foreignObject
                  x={-nodeWidth / 2 + 12}
                  y={-10}
                  width="20"
                  height="20"
                >
                  <NodeIcon
                    style={{ width: '20px', height: '20px', color: 'white' }}
                  />
                </foreignObject>

                {/* Node title */}
                <text
                  x={-nodeWidth / 2 + 44}
                  y={-2}
                  className="text-xs font-semibold fill-gray-700"
                  style={{ fontSize: '11px' }}
                >
                  {node.title.length > 18 ? node.title.slice(0, 18) + '...' : node.title}
                </text>

                {/* Node type label */}
                <text
                  x={-nodeWidth / 2 + 44}
                  y={12}
                  className="text-[9px] fill-gray-400 font-medium uppercase tracking-wide"
                >
                  {config.label}
                </text>

                {/* Hover indicator */}
                {isHovered && (
                  <circle
                    cx={nodeWidth / 2 - 12}
                    cy={0}
                    r="4"
                    fill={config.color}
                    className="animate-pulse"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="px-6 py-4 bg-secondary border-t border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 flex-wrap text-xs">
            <span className="font-medium text-muted-foreground">Legend:</span>
            {[
              { Icon: Sparkles, label: 'Query', color: 'hsl(var(--primary))' },
              { Icon: AlertCircle, label: 'Incident', color: 'hsl(var(--destructive))' },
              { Icon: GitBranch, label: 'PR', color: 'hsl(var(--success))' },
              { Icon: AlertTriangle, label: 'Root Cause', color: 'hsl(var(--warning))' },
              { Icon: CheckCircle2, label: 'Fix', color: 'hsl(var(--success))' },
            ].map(item => (
              <span key={item.label} className="flex items-center gap-1.5 text-muted-foreground">
                <item.Icon style={{ width: '14px', height: '14px', color: item.color }} />
                <span>{item.label}</span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-4 h-0.5 bg-destructive rounded"></span>
              <span>causal</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-0.5 bg-success rounded"></span>
              <span>fix</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-0.5 bg-warning rounded"></span>
              <span>trigger</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Federated Causal Graph - Hierarchical visualization of all connected entities
// =============================================================================
function FederatedCausalGraph({ causalGraph, onNodeClick }) {
  if (!causalGraph || !causalGraph.nodes || Object.keys(causalGraph.nodes).length === 0) {
    return null;
  }

  const { nodes_by_level, edges, level_counts, summary, sources_searched } = causalGraph;

  // Level display configuration
  const levelConfig = {
    incident: {
      label: 'Incidents & Alerts',
      color: 'red',
      icon: AlertCircle,
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700'
    },
    service: {
      label: 'Services',
      color: 'blue',
      icon: Activity,
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700'
    },
    change: {
      label: 'Changes & Deployments',
      color: 'purple',
      icon: GitBranch,
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700'
    },
    artifact: {
      label: 'Issues & Documentation',
      color: 'amber',
      icon: FileText,
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700'
    },
    code: {
      label: 'Code & Commits',
      color: 'green',
      icon: FileCode,
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700'
    },
    infrastructure: {
      label: 'Infrastructure',
      color: 'slate',
      icon: Wrench,
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      text: 'text-slate-700'
    },
    monitoring: {
      label: 'Monitoring & Logs',
      color: 'cyan',
      icon: TrendingUp,
      bg: 'bg-cyan-50',
      border: 'border-cyan-200',
      text: 'text-cyan-700'
    },
  };

  // Source icon mapping
  const sourceIcons = {
    github: '🐙',
    gitlab: '🦊',
    jira: '🎫',
    slack: '💬',
    linear: '📋',
    buildkite: '🏗️',
    datadog: '🐕',
    cribl: '📊',
    kubernetes: '☸️',
    terraform: '🌍',
    helm: '⛵',
    argocd: '🦑',
    internal: '🏠',
  };

  const orderedLevels = ['incident', 'service', 'change', 'artifact', 'code', 'infrastructure', 'monitoring'];

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Causal Graph</h3>
              <p className="text-white/70 text-sm">{summary}</p>
            </div>
          </div>
          {sources_searched && sources_searched.length > 0 && (
            <div className="flex items-center gap-2">
              {sources_searched.slice(0, 6).map(source => (
                <span key={source} className="text-lg" title={source}>
                  {sourceIcons[source] || '📁'}
                </span>
              ))}
              {sources_searched.length > 6 && (
                <span className="text-white/70 text-sm">+{sources_searched.length - 6}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Level counts summary */}
      {level_counts && Object.keys(level_counts).length > 0 && (
        <div className="px-5 py-3 bg-secondary border-b border-border flex flex-wrap gap-3">
          {Object.entries(level_counts).map(([level, count]) => {
            const config = levelConfig[level] || levelConfig.artifact;
            return (
              <span key={level} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                <config.icon className="w-3 h-3" />
                {count} {config.label.toLowerCase()}
              </span>
            );
          })}
        </div>
      )}

      {/* Hierarchical graph */}
      <div className="p-5 space-y-4">
        {orderedLevels.map(level => {
          const nodesAtLevel = nodes_by_level?.[level];
          if (!nodesAtLevel || nodesAtLevel.length === 0) return null;

          const config = levelConfig[level] || levelConfig.artifact;
          const LevelIcon = config.icon;

          return (
            <div key={level} className={`rounded-lg ${config.bg} ${config.border} border p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <LevelIcon className={`w-4 h-4 ${config.text}`} />
                <span className={`font-medium ${config.text} text-sm`}>{config.label}</span>
                <span className={`text-xs ${config.text} opacity-70`}>({nodesAtLevel.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {nodesAtLevel.slice(0, 9).map(node => (
                  <button
                    key={node.id}
                    onClick={() => onNodeClick?.(node)}
                    className="bg-card rounded-lg p-3 text-left shadow-sm border border-border hover:border-primary/30 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg flex-shrink-0">{sourceIcons[node.source] || '📁'}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground text-sm truncate group-hover:text-primary">
                          {node.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground capitalize">{node.source}</span>
                          {node.status && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${node.status === 'success' || node.status === 'merged' || node.status === 'closed'
                              ? 'bg-success/10 text-success'
                              : node.status === 'failure' || node.status === 'critical'
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-secondary text-muted-foreground'
                              }`}>
                              {node.status}
                            </span>
                          )}
                        </div>
                        {node.author && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">by {node.author}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                    </div>
                  </button>
                ))}
                {nodesAtLevel.length > 9 && (
                  <div className="bg-secondary rounded-lg p-3 flex items-center justify-center text-muted-foreground text-sm">
                    +{nodesAtLevel.length - 9} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edges summary */}
      {edges && edges.length > 0 && (
        <div className="px-5 py-3 bg-secondary border-t border-border">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">{edges.length}</span> relationships found between entities
          </p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LLM Analysis Panel - Shows AI-generated insights
// =============================================================================
function LLMAnalysisPanel({ analysis, isLoading, query }) {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = useCallback(() => {
    const content = typeof analysis === 'string' ? analysis :
      [analysis?.summary, analysis?.root_cause, analysis?.recommendations?.join('\n')].filter(Boolean).join('\n\n');
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [analysis]);

  // Loading state
  if (isLoading) {
    return (
      <div className="card overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">AI Analysis</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Analyzing your query...
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="h-4 bg-secondary rounded-full animate-pulse" style={{ width: '95%' }} />
            <div className="h-4 bg-secondary rounded-full animate-pulse" style={{ width: '80%' }} />
            <div className="h-4 bg-secondary rounded-full animate-pulse" style={{ width: '88%' }} />
            <div className="h-6 mt-4" />
            <div className="h-4 bg-secondary rounded-full animate-pulse" style={{ width: '70%' }} />
            <div className="h-4 bg-secondary rounded-full animate-pulse" style={{ width: '85%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  // Handle both string and object analysis
  const content = typeof analysis === 'string' ? analysis :
    [
      analysis.summary,
      analysis.root_cause && `**Root Cause:** ${analysis.root_cause}`,
      analysis.recommendations?.length && `## Recommendations\n${analysis.recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n')}`
    ].filter(Boolean).join('\n\n');

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">AI Analysis</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-success" />
              Analysis complete
            </p>
          </div>
        </div>

        <button
          onClick={handleCopyAll}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${copied
            ? 'bg-success/10 text-success'
            : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Query context */}
      {query && (
        <div className="px-6 py-3 bg-secondary border-b border-border">
          <p className="text-sm text-muted-foreground">
            <span className="text-muted-foreground/60">Query:</span> "{query}"
          </p>
        </div>
      )}

      {/* Main content */}
      <div className="p-6">
        <MarkdownRenderer content={content} />
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-secondary border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Powered by AI
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// Main Causal Chain Analysis Component
// =============================================================================
export { FederatedCausalGraph, LLMAnalysisPanel, DetailsModal, CodeChangesSection, CausalNodeCard, RootCauseGraph };

export default function CausalChainAnalysis({
  isOpen,
  onClose,
  investigationData,
  onAskFollowUp
}) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [llmAnalysis, setLlmAnalysis] = useState(null);
  const [isLoadingLLM, setIsLoadingLLM] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState('');

  // Extract data from investigation result
  const analysis = investigationData?.analysis;
  // Prefer incident field (which includes linked resources), fallback to matched_issue
  const incident = investigationData?.incident || investigationData?.matched_issue;
  const causalChain = analysis?.causal_chain;
  const triggeringChange = analysis?.triggering_change;
  const recommendedActions = analysis?.recommended_actions || [];
  const llmAnalysisText = investigationData?.llm_analysis;
  const hasError = investigationData?.error;
  const codeChanges = investigationData?.code_changes || [];
  const sourceType = investigationData?.source_type;
  // Extract federated causal graph
  const federatedCausalGraph = investigationData?.causal_graph;
  const searchedSources = investigationData?.searched_sources || [];

  // Reset LLM analysis when investigation data changes (Bug fix: stale state)
  React.useEffect(() => {
    setLlmAnalysis(null);
  }, [investigationData]);

  // Unified helper to check if a value is sample/fake data
  // Only checks for [SAMPLE] prefix which is the documented convention
  const isSampleData = useCallback((value) => {
    if (!value) return false; // null/undefined is not "sample data", just missing
    if (typeof value === 'string') {
      return value.startsWith('[SAMPLE]');
    }
    return false;
  }, []);

  // Build node data from investigation - only using real data
  const buildNodeData = useCallback(() => {
    const nodes = [];
    const sourceType = investigationData?.source_type;

    // Determine node type based on source type
    if (incident) {
      const props = incident.properties || incident;
      // Only show if it has a real ID (not sample data)
      if (props.id && !isSampleData(props.id)) {
        // Handle different source types
        if (sourceType === 'github_pr' || sourceType === 'gitlab_mr') {
          nodes.push({
            type: 'github_pr',
            data: {
              id: props.id,
              title: props.title || `PR #${props.number || '?'}`,
              number: props.number,
              state: props.state || (props.merged ? 'merged' : 'unknown'),
              merged: props.merged,
              author: props.author || props.user?.login || 'Unknown',
              author_avatar: props.author_avatar,
              description: props.body || props.description || 'No description available',
              created_at: props.created_at,
              updated_at: props.updated_at,
              merged_at: props.merged_at,
              time: props.created_at,
              changed_files: props.changed_files || 0,
              additions: props.additions || 0,
              deletions: props.deletions || 0,
              head_ref: props.head_ref,
              base_ref: props.base_ref,
              repo: props.repo || props.repo_key,
              url: props.url || props.html_url,
              source_type: sourceType,
              labels: props.labels || [],
              reviewers: props.reviewers || []
            }
          });
        } else if (sourceType === 'github_workflow') {
          nodes.push({
            type: 'github_workflow',
            data: {
              id: props.id,
              title: props.workflow_name || props.name || 'Workflow Run',
              run_number: props.run_number,
              conclusion: props.conclusion || props.status || 'unknown',
              branch: props.head_branch || props.branch || 'unknown',
              created_at: props.created_at,
              time: props.created_at,
              repo: props.repo || props.repo_key,
              url: props.url || props.html_url,
              source_type: sourceType
            }
          });
        } else {
          // Default: treat as incident
          nodes.push({
            type: 'incident',
            data: {
              id: props.id,
              title: props.title || 'Unknown Incident',
              severity: props.severity || 'unknown',
              status: props.status || 'Unknown',
              description: props.description || 'No description available',
              started_at: props.started_at,
              time: props.started_at,
              failure_rate: props.failure_rate,
              impact: props.impact,
              affected_services: props.affected_services || [],
              linked_resources: props.linked_resources || []
            }
          });
        }
      }
    }

    // Triggering change node - only if real triggering change exists with non-fake author
    if (triggeringChange && triggeringChange.id && !isSampleData(triggeringChange.author)) {
      nodes.push({
        type: 'github_commit',
        data: {
          id: triggeringChange.id,
          sha: triggeringChange.id,
          message: triggeringChange.message,
          title: triggeringChange.message,
          author: triggeringChange.author,
          timestamp: triggeringChange.timestamp,
          time: triggeringChange.time_before_incident_minutes
            ? `${triggeringChange.time_before_incident_minutes}m before incident`
            : undefined,
          files_changed: triggeringChange.files_changed,
          service: triggeringChange.service,
          pr_number: triggeringChange.pr_number,
          change_summary: triggeringChange.change_summary,
          url: triggeringChange.url
        }
      });
    }

    // Root cause node from causal chain - only if real root cause identified
    if (causalChain?.root_cause && !isSampleData(causalChain.root_cause)) {
      const rootCauseLink = causalChain.links?.find(l => l.target_service === causalChain.root_cause) ||
        causalChain.links?.[0];
      nodes.push({
        type: 'root_cause',
        data: {
          title: rootCauseLink?.evidence || `Issue in ${causalChain.root_cause}`,
          service: causalChain.root_cause,
          confidence: rootCauseLink?.confidence,
          evidence: rootCauseLink?.evidence,
          impact: rootCauseLink?.impact,
          metrics: rootCauseLink?.metrics
        }
      });
    }

    // Metric anomaly nodes from causal links - only real ones
    if (causalChain?.links) {
      causalChain.links.slice(0, 2).forEach((link) => {
        if (link.target_service && link.target_service !== causalChain.root_cause && !isSampleData(link.target_service)) {
          nodes.push({
            type: 'metric',
            data: {
              title: link.signal_type || 'Metric Anomaly',
              service: link.target_service,
              confidence: link.confidence,
              change: link.evidence
            }
          });
        }
      });
    }

    // Add Jira ticket only if linked in incident data
    const incidentProps = incident?.properties || incident || {};
    const linkedJira = incidentProps.jira_ticket || incidentProps.linked_jira;
    if (linkedJira && !isSampleData(linkedJira.key || linkedJira.id)) {
      nodes.push({
        type: 'jira',
        data: {
          key: linkedJira.key || linkedJira.id,
          id: linkedJira.key || linkedJira.id,
          summary: linkedJira.summary || linkedJira.title,
          title: linkedJira.summary || linkedJira.title,
          status: linkedJira.status,
          priority: linkedJira.priority,
          url: linkedJira.url
        }
      });
    }

    // Add Slack discussion only if linked in incident data
    const linkedSlack = incidentProps.slack_thread || incidentProps.linked_slack;
    if (linkedSlack && !isSampleData(linkedSlack.channel)) {
      nodes.push({
        type: 'slack',
        data: {
          channel: linkedSlack.channel,
          message_count: linkedSlack.message_count,
          participants: linkedSlack.participants,
          key_message: linkedSlack.key_message,
          key_points: linkedSlack.key_points,
          url: linkedSlack.url
        }
      });
    }

    // Add runbook only if available from recommended actions or incident data
    const linkedRunbook = incidentProps.runbook || incidentProps.linked_runbook;
    if (linkedRunbook && !isSampleData(linkedRunbook.id)) {
      nodes.push({
        type: 'runbook',
        data: {
          id: linkedRunbook.id,
          title: linkedRunbook.title,
          steps: linkedRunbook.steps,
          summary: linkedRunbook.summary,
          url: linkedRunbook.url
        }
      });
    }

    // Add suggested fixes from recommended actions - only if there are real actions
    if (recommendedActions.length > 0) {
      const action = recommendedActions[0];
      nodes.push({
        type: 'suggested_fix',
        data: {
          title: action.description || 'Suggested Fix',
          action: action.description,
          description: action.command ? 'Execute the recommended command' : 'Follow the recommended action',
          file_reference: action.file_reference,
          options: action.command ? [
            {
              label: action.action_type === 'revert' ? 'Revert Change' : 'Apply Fix',
              command: action.command
            }
          ] : [],
          estimated_impact: action.estimated_impact
        }
      });
    }

    return nodes;
  }, [incident, triggeringChange, causalChain, recommendedActions, isSampleData, investigationData]);

  const nodes = buildNodeData();

  const handleNodeClick = (type, data) => {
    setSelectedNode({ type, data });
    setDetailsModalOpen(true);
  };

  const handleAskFollowUp = async () => {
    if (!followUpQuestion.trim() || !onAskFollowUp) return;

    setIsLoadingLLM(true);
    try {
      const response = await onAskFollowUp(followUpQuestion);
      setLlmAnalysis(response);
    } catch (error) {
      console.error('Follow-up question failed:', error);
    } finally {
      setIsLoadingLLM(false);
      setFollowUpQuestion('');
    }
  };

  // Build code preview data - only show if we have real triggering change with files
  const codePreviewData = triggeringChange &&
    triggeringChange.id &&
    triggeringChange.files_changed?.length > 0 &&
    !isSampleData(triggeringChange.author)
    ? {
      service: triggeringChange.service,
      file_path: triggeringChange.files_changed[0],
      commit_sha: triggeringChange.id,
      author: triggeringChange.author,
      commit_info: true,
      code_lines: triggeringChange.code_diff
        ? triggeringChange.code_diff.split('\n').map((line, idx) => ({
          number: idx + 1,
          content: line,
          type: line.startsWith('+') ? 'added' : line.startsWith('-') ? 'removed' : line.startsWith('#') ? 'comment' : undefined
        }))
        : null,
      related_pr: triggeringChange.pr_number && triggeringChange.pr_url
        ? {
          number: triggeringChange.pr_number,
          title: triggeringChange.pr_title || `PR #${triggeringChange.pr_number}`,
          url: triggeringChange.pr_url
        }
        : null
    } : null;

  if (!isOpen) return null;

  return (
    <div className="w-full">
      {/* Inline Investigation Panel - No modal overlay */}
      <div className="card mb-6 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Causal Chain Analysis</h2>
                <p className="text-white/70 text-sm">AI-powered investigation results</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              Back to Search
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Query Context - Always show when query exists */}
          {investigationData?.query && (
            <div className="bg-primary/5 rounded-xl border border-primary/20 px-6 py-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-primary font-medium uppercase tracking-wide">Investigation Query</p>
                  <p className="text-foreground font-medium">"{investigationData.query}"</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty State - Only show when no nodes AND no LLM analysis AND no causal graph data */}
          {nodes.length === 0 && !hasError && !llmAnalysisText && !(federatedCausalGraph && federatedCausalGraph.total_entities > 0) && (
            <div className="bg-secondary rounded-xl border border-border p-8 mb-6">
              <div className="max-w-md mx-auto text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Matching Data Found</h3>
                <p className="text-muted-foreground mb-6">
                  {investigationData?.query
                    ? 'No incidents, linked resources, or runbooks were found for this query.'
                    : 'No incidents, linked resources, or runbooks were found for this query.'}
                </p>
                <div className="bg-card rounded-lg border border-border p-4 text-left">
                  <p className="text-sm font-medium text-foreground mb-3">To see data here, you can:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                      <span>Connect data sources (GitHub, Jira, Slack) in Integrations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                      <span>Scan your repositories with the OnCall Scanner</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                      <span>Search for a specific incident ID (e.g., "INC-2025-001")</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div className="bg-red-50 rounded-xl border border-red-200 p-8 mb-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-700 mb-2">Investigation Error</h3>
              <p className="text-red-600">{hasError}</p>
            </div>
          )}

          {/* Causal Chain Visualization - Only show if we have nodes */}
          {nodes.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              {/* Dynamic grid layout based on available data */}
              <div className="space-y-6">
                {/* Row 1: Main causal flow - Incident/PR/Workflow/Commit -> Root Cause -> Effects */}
                {(() => {
                  const incidentNode = nodes.find(n => n.type === 'incident');
                  const prNode = nodes.find(n => n.type === 'github_pr');
                  const workflowNode = nodes.find(n => n.type === 'github_workflow');
                  const commitNode = nodes.find(n => n.type === 'github_commit');
                  const rootCauseNode = nodes.find(n => n.type === 'root_cause');
                  const metricNode = nodes.find(n => n.type === 'metric');
                  const mainSourceNode = incidentNode || prNode || workflowNode;
                  const hasMainFlow = mainSourceNode || commitNode || rootCauseNode || metricNode;

                  if (!hasMainFlow) return null;

                  return (
                    <div className="flex items-center justify-center gap-4 flex-wrap">
                      {/* Source: Incident, PR, Workflow, or Commit */}
                      {(mainSourceNode || commitNode) && (
                        <div className="flex flex-col gap-3 min-w-[200px]">
                          {incidentNode && (
                            <CausalNodeCard
                              type="incident"
                              data={incidentNode.data}
                              onClick={handleNodeClick}
                            />
                          )}
                          {prNode && (
                            <CausalNodeCard
                              type="github_pr"
                              data={prNode.data}
                              onClick={handleNodeClick}
                            />
                          )}
                          {workflowNode && (
                            <CausalNodeCard
                              type="github_workflow"
                              data={workflowNode.data}
                              onClick={handleNodeClick}
                            />
                          )}
                          {commitNode && (
                            <>
                              {mainSourceNode && (
                                <div className="flex items-center gap-1 text-xs text-amber-600 font-medium pl-2">
                                  <Zap className="w-3 h-3" />
                                  triggered by
                                </div>
                              )}
                              <CausalNodeCard
                                type="github_commit"
                                data={commitNode.data}
                                onClick={handleNodeClick}
                              />
                            </>
                          )}
                        </div>
                      )}

                      {/* Arrow to Root Cause */}
                      {(mainSourceNode || commitNode) && rootCauseNode && (
                        <ConnectionArrow label="caused by" />
                      )}

                      {/* Root Cause - Highlighted */}
                      {rootCauseNode && (
                        <CausalNodeCard
                          type="root_cause"
                          data={rootCauseNode.data}
                          onClick={handleNodeClick}
                          isHighlighted
                        />
                      )}

                      {/* Arrow to Effects */}
                      {rootCauseNode && metricNode && (
                        <ConnectionArrow label="affected" />
                      )}

                      {/* Effects: Metrics */}
                      {metricNode && (
                        <CausalNodeCard
                          type="metric"
                          data={metricNode.data}
                          onClick={handleNodeClick}
                        />
                      )}
                    </div>
                  );
                })()}

                {/* Row 2: Related Resources - Jira, Slack, Runbook */}
                {(() => {
                  const jiraNode = nodes.find(n => n.type === 'jira');
                  const slackNode = nodes.find(n => n.type === 'slack');
                  const runbookNode = nodes.find(n => n.type === 'runbook');
                  const hasResources = jiraNode || slackNode || runbookNode;

                  if (!hasResources) return null;

                  return (
                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                        Related Resources
                      </h4>
                      <div className="flex items-start gap-4 flex-wrap">
                        {jiraNode && (
                          <CausalNodeCard
                            type="jira"
                            data={jiraNode.data}
                            onClick={handleNodeClick}
                          />
                        )}
                        {slackNode && (
                          <CausalNodeCard
                            type="slack"
                            data={slackNode.data}
                            onClick={handleNodeClick}
                          />
                        )}
                        {runbookNode && (
                          <CausalNodeCard
                            type="runbook"
                            data={runbookNode.data}
                            onClick={handleNodeClick}
                          />
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Row 3: Suggested Fixes */}
                {(() => {
                  const fixNode = nodes.find(n => n.type === 'suggested_fix');
                  if (!fixNode) return null;

                  return (
                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                        Recommended Actions
                      </h4>
                      <CausalNodeCard
                        type="suggested_fix"
                        data={fixNode.data}
                        onClick={handleNodeClick}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Root Cause Analysis Graph - Always show when we have data */}
          {(nodes.length > 0 || llmAnalysisText) && (
            <RootCauseGraph
              nodes={nodes}
              analysis={llmAnalysisText}
              query={investigationData?.query}
              onNodeClick={handleNodeClick}
            />
          )}

          {/* Code Preview Section */}
          {codePreviewData && (
            <div className="mb-6">
              <h2 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <GitBranch className="w-4 h-4" />
                GitHub {codePreviewData.service}/{codePreviewData.file_path}
                <a href="#" className="text-blue-600 hover:underline ml-1">
                  <ExternalLink className="w-3 h-3 inline" />
                </a>
              </h2>
              <CodePreviewPanel data={codePreviewData} />
            </div>
          )}

          {/* Code Changes Section */}
          <CodeChangesSection
            changes={codeChanges}
            sourceType={sourceType}
          />

          {/* Federated Causal Graph - Shows all connected entities */}
          {federatedCausalGraph && federatedCausalGraph.total_entities > 0 && (
            <div className="mb-6">
              <FederatedCausalGraph
                causalGraph={federatedCausalGraph}
                onNodeClick={(node) => {
                  setSelectedNode({
                    type: node.node_type,
                    data: {
                      id: node.id,
                      title: node.title,
                      ...node.properties,
                      source: node.source,
                      status: node.status,
                      author: node.author,
                      url: node.url
                    }
                  });
                  setDetailsModalOpen(true);
                }}
              />
            </div>
          )}

          {/* LLM Analysis Panel */}
          <LLMAnalysisPanel
            analysis={llmAnalysis || llmAnalysisText}
            isLoading={isLoadingLLM}
            query={investigationData?.query}
          />

          {/* Follow-up Questions */}
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MessageSquare className="w-4 h-4" />
              Ask a follow-up question
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFollowUpQuestion("What's wrong with checkout service?")}
                className="px-3 py-2 text-sm text-foreground bg-card border border-border rounded-lg hover:bg-secondary transition-colors"
              >
                What's wrong with checkout service?
              </button>
              <button
                onClick={() => setFollowUpQuestion("Suggested Fix: Page on-call engineer")}
                className="px-3 py-2 text-sm text-foreground bg-card border border-border rounded-lg hover:bg-secondary transition-colors"
              >
                Suggested Fix: Page on-call engineer
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={followUpQuestion}
                onChange={(e) => setFollowUpQuestion(e.target.value)}
                placeholder="Type your question..."
                className="input flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleAskFollowUp()}
              />
              <button
                onClick={handleAskFollowUp}
                disabled={!followUpQuestion.trim() || isLoadingLLM}
                className="btn btn-primary"
              >
                {isLoadingLLM ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Ask
              </button>
            </div>
          </div>
        </div>

        {/* Details Modal */}
        <DetailsModal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          data={selectedNode?.data}
          type={selectedNode?.type}
        />
      </div>
    </div>
  );
}
